import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';

interface Step4FieldsProps {
    dbFormFields: any[];
    setDbFormFields: React.Dispatch<React.SetStateAction<any[]>>;
    formFieldTypes: any[];
    signers: any[];
    mapProperties: any[];
    readonly?: boolean;
}

export const Step4Fields: React.FC<Step4FieldsProps> = ({
    dbFormFields,
    setDbFormFields,
    formFieldTypes,
    signers,
    mapProperties,
    readonly = false
}) => {
    const knownSigners = React.useMemo(
        () => (signers || []).filter(signer => signer?.recipientInfoStatus !== 'UNKNOWN'),
        [signers]
    );
    const knownSignerIds = React.useMemo(
        () => new Set(knownSigners.map(signer => String(signer.partyId || '')).filter(Boolean)),
        [knownSigners]
    );

    React.useEffect(() => {
        if (!signers?.length || !dbFormFields?.length) return;

        setDbFormFields(prev => {
            let changed = false;
            const next = prev.map(field => {
                if (!field.partyId || knownSignerIds.has(String(field.partyId))) return field;

                changed = true;
                return {
                    ...field,
                    partyId: null,
                    roleTypeId: null,
                    mapPropertyEnumId: null
                };
            });

            return changed ? next : prev;
        });
    }, [dbFormFields?.length, knownSignerIds, setDbFormFields, signers?.length]);

    return (
        <div className="flex flex-column h-full">
            <div className="flex-1 overflow-auto border-round-xl shadow-4 bg-white p-2">
                <DataTable value={dbFormFields} scrollable scrollHeight="flex" 
                    className="p-datatable-sm" 
                    rowClassName={() => 'hover:surface-100 transition-colors transition-duration-200'}
                    responsiveLayout="scroll"
                >
                    <Column field="fieldName" header="Tên biến" style={{ width: '14%' }} 
                        body={(rowData) => <span className="font-mono text-primary font-bold">{rowData.fieldName}</span>} 
                    />
                    <Column header="Nhãn hiển thị" style={{ width: '19%' }} body={(rowData, options) => (
                        <InputText
                            value={rowData.title || ''}
                            onChange={(e) => {
                                setDbFormFields(prev => {
                                    const updated = [...prev];
                                    updated[options.rowIndex] = { ...updated[options.rowIndex], title: e.target.value };
                                    return updated;
                                });
                            }}
                            placeholder="Nhãn hiển thị"
                            className="w-full p-inputtext-sm border-round-lg shadow-1"
                            disabled={readonly}
                        />
                    )} />
                    <Column header="Kiểu dữ liệu" style={{ width: '17%' }} body={(rowData, options) => {
                        const isDateMap = rowData.mapPropertyEnumId === 'MapProp_AgtDate';
                        const filteredOptions = isDateMap 
                            ? formFieldTypes.filter(opt => opt.value.toLowerCase().includes('date'))
                            : formFieldTypes;

                        return (
                            <Dropdown
                                value={rowData.fieldTypeEnumId}
                                options={filteredOptions}
                                onChange={(e) => {
                                    setDbFormFields(prev => {
                                        const updated = [...prev];
                                        updated[options.rowIndex] = { ...updated[options.rowIndex], fieldTypeEnumId: e.value };
                                        return updated;
                                    });
                                }}
                                placeholder="Chọn kiểu"
                                className="w-full p-inputtext-sm border-round-lg shadow-1"
                                disabled={readonly}
                                tooltip={isDateMap ? 'Vui lòng chọn định dạng ngày tháng cho Ngày hợp đồng' : undefined}
                            />
                        );
                    }} />
                    <Column header="Đối tượng" style={{ width: '19%' }} body={(rowData, options) => {
                        const selectedProp = mapProperties?.find(p => p.enumId === rowData.mapPropertyEnumId); // Changed p.value to p.enumId based on mapProperties structure
                        const isGeneralInfo = selectedProp?.optionValue === 'N';

                        const signerOptions = knownSigners.map(signer => ({
                            ...signer,
                            label: signer.partyName || signer.partyId,
                            value: signer.partyId
                        }));
                        return (
                            <Dropdown
                                value={rowData.partyId}
                                options={signerOptions}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => {
                                    setDbFormFields(prev => {
                                        const updated = [...prev];
                                        updated[options.rowIndex] = {
                                            ...updated[options.rowIndex],
                                            partyId: e.value,
                                            roleTypeId: e.value ? 'AgreementSigner' : null
                                        };
                                        return updated;
                                    });
                                }}
                                placeholder="Chọn đối tượng"
                                className="w-full p-inputtext-sm border-round-lg shadow-1"
                                disabled={readonly || isGeneralInfo}
                                showClear
                            />
                        );
                    }} />
                    <Column header="Thuộc tính" style={{ width: '23%' }} body={(rowData, options) => {
                        const isGeneralInfoProperty = (property: any) => property?.optionValue === 'N';
                        
                        const filteredMapProperties = rowData.partyId
                            ? mapProperties?.filter(p => !isGeneralInfoProperty(p)) // Có đối tượng -> Chỉ hiện thuộc tính Đối tượng
                            : mapProperties?.filter(p => isGeneralInfoProperty(p)); // Chưa có đối tượng -> Chỉ hiện Thông tin chung

                        const isDateField = rowData.fieldTypeEnumId?.toLowerCase().includes('date');
                        const optionsFiltered = filteredMapProperties?.filter(p => {
                            // 1. Phải luôn hiển thị option mà dòng hiện tại đang chọn, 
                            // nếu không Dropdown sẽ bị trắng xoá giá trị đã chọn.
                            if (p.enumId === rowData.mapPropertyEnumId) return true;

                            // 2. Kiểm tra Type chéo nhau (Date vs Text)
                            const isDateField = rowData.fieldTypeEnumId?.toLowerCase().includes('date');
                            if (isDateField) {
                                if (p.enumId !== 'MapProp_AgtDate') return false;
                            } else if (rowData.fieldTypeEnumId) {
                                if (p.enumId === 'MapProp_AgtDate') return false;
                            }

                            // 3. Kiểm tra Đã được chọn ở dòng khác chưa
                            const currentRowParty = rowData.partyId || null;
                            const isAlreadySelected = dbFormFields.some((field, idx) => {
                                if (idx === options.rowIndex) return false;
                                if (!field.mapPropertyEnumId) return false;
                                
                                const fieldParty = field.partyId || null;
                                return field.mapPropertyEnumId === p.enumId && fieldParty === currentRowParty;
                            });
                            
                            return !isAlreadySelected;
                        });

                        return (
                            <Dropdown
                                value={rowData.mapPropertyEnumId}
                                options={optionsFiltered}
                            optionLabel="description"
                            optionValue="enumId"
                            onChange={(e) => {
                                setDbFormFields(prev => {
                                    const updated = [...prev];
                                    const selectedProp = mapProperties?.find(p => p.enumId === e.value);
                                    
                                    updated[options.rowIndex] = { ...updated[options.rowIndex], mapPropertyEnumId: e.value };
                                    
                                    // Nếu là thông tin chung (N), xóa đối tượng
                                    if (selectedProp?.optionValue === 'N') {
                                        updated[options.rowIndex].partyId = null;
                                        updated[options.rowIndex].roleTypeId = null;
                                    }

                                    // Nếu user chọn Ngày hợp đồng, đảm bảo kiểu dữ liệu là Ngày tháng
                                    if (e.value === 'MapProp_AgtDate') {
                                        const currentType = updated[options.rowIndex].fieldTypeEnumId;
                                        if (!currentType || !currentType.toLowerCase().includes('date')) {
                                            updated[options.rowIndex].fieldTypeEnumId = 'DBFFAT_date-long'; 
                                        }
                                        updated[options.rowIndex].isRequired = true;
                                    }

                                    // Tự động bắt buộc cho MST và Email
                                    if (e.value === 'MapProp_TaxId' || e.value === 'MapProp_Email') {
                                        updated[options.rowIndex].isRequired = true;
                                    }

                                    return updated;
                                });
                            }}
                                placeholder="Chọn thuộc tính"
                                className="w-full p-inputtext-sm border-round-lg shadow-1"
                                disabled={readonly}
                                showClear
                            />
                        );
                    }} />
                    <Column header={
                        <div className="flex flex-column align-items-center gap-1">
                            <span className="text-xs white-space-nowrap">Bắt buộc</span>
                            <Checkbox
                                checked={dbFormFields.length > 0 && dbFormFields.every(f => f.isRequired)}
                                onChange={(e) => {
                                    if (readonly) return;
                                    const checked = e.checked ?? false;
                                    setDbFormFields(prev => prev.map(f => ({ 
                                        ...f, 
                                        isRequired: (f.mapPropertyEnumId === 'MapProp_TaxId' || f.mapPropertyEnumId === 'MapProp_Email') ? true : checked 
                                    })));
                                }}
                                disabled={readonly}
                                className="shadow-1"
                            />
                        </div>
                    } style={{ width: '8%' }} className="text-center" body={(rowData, options) => (
                        <Checkbox
                            checked={rowData.isRequired}
                            onChange={(e) => {
                                if (readonly) return;
                                const checked = e.checked ?? false;
                                setDbFormFields(prev => {
                                    const updated = [...prev];
                                    updated[options.rowIndex] = { ...updated[options.rowIndex], isRequired: checked };
                                    return updated;
                                });
                            }}
                            disabled={readonly || rowData.mapPropertyEnumId === 'MapProp_TaxId' || rowData.mapPropertyEnumId === 'MapProp_Email'}
                            className="shadow-1"
                        />
                    )} />
                </DataTable>
            </div>
        </div>
    );
};
